/**
 * Mock host for plugins that use bridge hooks (`useBridgeTheme`,
 * `useBridgeLocale`, `useBridgeA11y`, etc.) during local-dev or contract
 * testing.
 *
 * Wraps {@link DeclarativeMockHost} and wires a {@link BridgeClientContext}
 * provider so any descendant bridge hook resolves against the supplied
 * `bridgeTransport` instead of throwing "no bridge client available".
 *
 * For tests where bridge state must be driven externally (push a new theme,
 * assert that a component re-renders), pass an {@link InMemoryBridgeTransport}
 * instance and call `transport.pushTheme(...)` wrapped in `act()`.
 */
import { type ReactElement } from "react";
import { DeclarativeMockHost, type DeclarativeMockHostProps } from "./DeclarativeMockHost";
import { BridgeClientContext } from "../plugin/BridgeClientContext";
import type { PortBridgeClient } from "../plugin/bridge-client";

export interface WorkerMockHostProps extends DeclarativeMockHostProps {
  /**
   * The bridge transport to wire into context. Pass an
   * {@link InMemoryBridgeTransport} for tests; pass a
   * `createPortBridgeClient(port)` instance for postMessage integration tests.
   */
  bridgeTransport: PortBridgeClient;
}

/**
 * Mock host that combines the SDUI declarative pipeline with bridge context.
 *
 * Rendering contract (same as DeclarativeMockHost):
 * - `defaultResourceUri` present in `resources` → renders the SDUI tree.
 * - URI absent → renders `children` instead (tool-invocation stubs, etc).
 *
 * Bridge contract:
 * - All `useBridgeTheme`, `useBridgeLocale`, etc. hooks in the subtree resolve
 *   against `bridgeTransport`.
 */
export function WorkerMockHost(props: WorkerMockHostProps): ReactElement {
  const { bridgeTransport, ...declarativeProps } = props;

  return (
    <BridgeClientContext.Provider value={bridgeTransport}>
      <DeclarativeMockHost {...declarativeProps} />
    </BridgeClientContext.Provider>
  );
}
